<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { onBeforeRouteLeave, useRouter, useRoute } from 'vue-router';
import PageHeader from 'dashboard/routes/dashboard/settings/SettingsSubPageHeader.vue';
import { useScheduling } from 'dashboard/composables/useScheduling';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-shadcn/new-york/ui/tabs';
import ScheduleConfigureForm from './ScheduleConfigureForm.vue';
import ScheduleLimitIndicator from './ScheduleLimitIndicator.vue';
import SchedulePreviewSummary from './SchedulePreviewSummary.vue';
import ScheduleStatsCards from './ScheduleStatsCards.vue';
import ScheduleActionBar from './ScheduleActionBar.vue';

const props = defineProps({
  templateName: {
    type: String,
    default: '',
  },
});
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const {
  schedulingState,
  v$,
  currentDate,
  generateScheduledDates,
  scheduledDatesPreview,
  scheduleCount,
  isWithinLimit,
  MAX_SCHEDULES,
} = useScheduling();

const campaignData = computed(
  () => store.getters['campaigns/getWhatsappOficialCampaignData']
);
const isLoadingFromStore = ref(false);
const hasLoadedInitialData = ref(false);
const activeTab = ref('configure');

const tabs = [
  {
    id: 'configure',
    label: 'Configurar Agendamento',
    shortLabel: 'Configurar',
    icon: '⚙️',
  },
  {
    id: 'preview',
    label: 'Visualizar Agenda',
    shortLabel: 'Visualizar',
    icon: '📅',
  },
];

onBeforeRouteLeave((to, from, next) => {
  if (
    to.name === 'campaigns_whatsapp_official_home' ||
    !to.name?.includes('campaigns_whatsapp_official')
  ) {
    store.dispatch('campaigns/clearCampaignData');
  }
  next();
});

const showPreview = computed(() => {
  const hasBasicData =
    schedulingState.startDate &&
    schedulingState.endDate &&
    schedulingState.fromTime &&
    schedulingState.recurrenceType &&
    (schedulingState.recurrenceType !== 'custom' ||
      schedulingState.selectedDays.length > 0);
  return hasBasicData && isWithinLimit.value;
});

const scheduleInsights = computed(() => {
  if (!showPreview.value) return null;

  const totalDates = scheduledDatesPreview.value.length;
  const startDate = new Date(schedulingState.startDate);
  const endDate = new Date(schedulingState.endDate);
  const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  let frequency = '';
  switch (schedulingState.recurrenceType) {
    case 'daily':
      frequency = 'Todos os dias';
      break;
    case 'weekly':
      frequency = 'Semanalmente';
      break;
    case 'monthly':
      frequency = 'Mensalmente';
      break;
    case 'custom': {
      const dayNames = schedulingState.selectedDays
        .map(d => Object.values(schedulingState.dayNames)[d].slice(0, 3))
        .join(', ');
      frequency = dayNames;
      break;
    }
    default:
      break;
  }

  return { totalDates, daysDiff, frequency };
});

const tabValidation = computed(() => ({
  configure: showPreview.value,
  preview: showPreview.value,
}));

const canNavigateToTab = tabId => {
  if (tabId === 'configure') return true;
  return tabValidation.value.configure;
};

let saveTimeout = null;
const saveSchedulingData = () => {
  if (isLoadingFromStore.value) return;
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    store.dispatch('campaigns/saveCampaignData', {
      schedule: {
        startDate: schedulingState.startDate,
        endDate: schedulingState.endDate,
        fromTime: schedulingState.fromTime,
        recurrenceType: schedulingState.recurrenceType,
        selectedDays: [...(schedulingState.selectedDays || [])],
      },
    });
  }, 300);
};

const onTabChange = tabId => {
  if (!canNavigateToTab(tabId)) return;
  saveSchedulingData();
  activeTab.value = tabId;
};

const onBack = () => {
  saveSchedulingData();
  const sourceType = campaignData.value?.filterData?.source_type;
  const routeName =
    sourceType === 'google_sheets'
      ? 'campaigns_whatsapp_official_configure_google_sheets'
      : 'campaigns_whatsapp_official_configure_list';
  router.push({
    name: routeName,
    params: { accountId: route.params.accountId },
    query: { templateName: route.query.templateName || props.templateName },
  });
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const scheduledDates = generateScheduledDates();
  await store.dispatch('campaigns/saveCampaignData', {
    schedule: {
      startDate: schedulingState.startDate,
      endDate: schedulingState.endDate,
      fromTime: schedulingState.fromTime,
      recurrenceType: schedulingState.recurrenceType,
      selectedDays:
        schedulingState.recurrenceType === 'custom'
          ? schedulingState.selectedDays
          : [],
      scheduledDates,
    },
  });

  const queryParams = { templateName: props.templateName };
  if (route.query.sourceType) queryParams.sourceType = route.query.sourceType;

  router.push({
    name: 'campaigns_whatsapp_official_finish',
    params: { accountId: store.state.auth.accountId },
    query: queryParams,
  });
};

watch(
  () => [
    schedulingState.startDate,
    schedulingState.endDate,
    schedulingState.fromTime,
    schedulingState.recurrenceType,
  ],
  () => {
    if (hasLoadedInitialData.value && !isLoadingFromStore.value)
      saveSchedulingData();
  }
);

watch(
  () => schedulingState.selectedDays,
  () => {
    if (hasLoadedInitialData.value && !isLoadingFromStore.value)
      saveSchedulingData();
  },
  { deep: true }
);

watch(
  () => campaignData.value?.schedule,
  async newSchedule => {
    if (!newSchedule || isLoadingFromStore.value) return;
    isLoadingFromStore.value = true;
    try {
      await nextTick();
      if (
        newSchedule.startDate !== undefined &&
        newSchedule.startDate !== schedulingState.startDate
      )
        schedulingState.startDate = newSchedule.startDate;
      if (
        newSchedule.endDate !== undefined &&
        newSchedule.endDate !== schedulingState.endDate
      )
        schedulingState.endDate = newSchedule.endDate;
      if (
        newSchedule.fromTime !== undefined &&
        newSchedule.fromTime !== schedulingState.fromTime
      )
        schedulingState.fromTime = newSchedule.fromTime;
      if (
        newSchedule.recurrenceType !== undefined &&
        newSchedule.recurrenceType !== schedulingState.recurrenceType
      )
        schedulingState.recurrenceType = newSchedule.recurrenceType;
      if (
        newSchedule.selectedDays !== undefined &&
        Array.isArray(newSchedule.selectedDays)
      )
        schedulingState.selectedDays = [...newSchedule.selectedDays];
      hasLoadedInitialData.value = true;
    } finally {
      setTimeout(() => {
        isLoadingFromStore.value = false;
      }, 100);
    }
  },
  { immediate: true, deep: true }
);

onMounted(() => {
  setTimeout(() => {
    hasLoadedInitialData.value = true;
  }, 500);
});
</script>

<template>
  <div
    class="border border-woot-400 dark:border-woot-400 bg-n-background min-h-[100px] p-6 w-full min-w-0 flex-1 overflow-y-auto"
  >
    <!-- Header -->
    <div class="mb-4 sm:mb-6">
      <PageHeader
        class="w-full max-w-4xl"
        :header-title="$t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.TITLE')"
        :header-content="$t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.BODY')"
      />
    </div>

    <!-- Main Content -->
    <div class="w-full">
      <Tabs
        :model-value="activeTab"
        class="w-full"
        @update:model-value="onTabChange"
      >
        <!-- Tab Navigation -->
        <TabsList
          class="grid w-full grid-cols-2 p-1 rounded-lg bg-white dark:bg-slate-900 border border-woot-400 dark:border-woot-400"
        >
          <TabsTrigger
            v-for="tab in tabs"
            :key="tab.id"
            :value="tab.id"
            :disabled="!canNavigateToTab(tab.id)"
            class="relative transition-all duration-200 text-xs sm:text-sm font-medium"
            :class="[
              !canNavigateToTab(tab.id) && 'opacity-50 cursor-not-allowed',
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 sm:gap-2 min-w-0"
            >
              <span class="text-sm sm:text-base flex-shrink-0">{{
                tab.icon
              }}</span>
              <span class="hidden sm:block truncate">{{ tab.label }}</span>
              <span class="sm:hidden truncate">{{ tab.shortLabel }}</span>
              <span
                v-if="tabValidation[tab.id]"
                class="text-green-500 dark:text-green-400 ml-1 flex-shrink-0"
              >
                {{
                  $t(
                    'CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.TAB_CONFIGURE_VALID'
                  )
                }}
              </span>
            </div>
          </TabsTrigger>
        </TabsList>

        <!-- Tab Content -->
        <div class="mt-4">
          <!-- Configure Tab -->
          <TabsContent value="configure" class="space-y-4 sm:space-y-6">
            <div
              class="min-h-[475px] bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 flex flex-col justify-between"
            >
              <ScheduleConfigureForm
                :start-date="schedulingState.startDate"
                :end-date="schedulingState.endDate"
                :from-time="schedulingState.fromTime"
                :recurrence-type="schedulingState.recurrenceType"
                :selected-days="schedulingState.selectedDays"
                :day-names="schedulingState.dayNames"
                :current-date="currentDate"
                :v$="v$"
                @update:start-date="schedulingState.startDate = $event"
                @update:end-date="schedulingState.endDate = $event"
                @update:from-time="schedulingState.fromTime = $event"
                @update:recurrence-type="
                  schedulingState.recurrenceType = $event
                "
                @update:selected-days="schedulingState.selectedDays = $event"
              />

              <div v-if="scheduleCount > 0" class="px-6 pb-2">
                <ScheduleLimitIndicator
                  :schedule-count="scheduleCount"
                  :max-schedules="MAX_SCHEDULES"
                  :is-within-limit="isWithinLimit"
                />
              </div>

              <ScheduleActionBar
                :back-label="t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.BACK')"
                :primary-label="
                  t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.PREVIEW')
                "
                :primary-disabled="!showPreview"
                primary-variant="next"
                @back="onBack"
                @primary="onTabChange('preview')"
              />
            </div>
          </TabsContent>

          <!-- Preview Tab -->
          <TabsContent value="preview">
            <div
              class="min-h-[475px] bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 flex flex-col justify-between"
            >
              <div
                v-if="showPreview && scheduleInsights"
                class="space-y-5 flex-1 px-6 mt-6"
              >
                <SchedulePreviewSummary
                  :start-date="schedulingState.startDate"
                  :end-date="schedulingState.endDate"
                  :from-time="schedulingState.fromTime"
                />

                <ScheduleStatsCards
                  :total-dates="scheduleInsights.totalDates"
                  :days-diff="scheduleInsights.daysDiff"
                  :frequency="scheduleInsights.frequency"
                />
              </div>

              <ScheduleActionBar
                :back-label="
                  t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.BACK_TO_CONFIG')
                "
                back-label-short="Voltar"
                :primary-label="
                  t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.CONFIRM')
                "
                :primary-disabled="!showPreview"
                primary-variant="confirm"
                @back="onTabChange('configure')"
                @primary="handleSubmit"
              />
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  </div>
</template>
